import { useEffect, useState } from "react";
import {
    Eye, X, Search, ArrowLeft, User, Calendar as CalendarIcon,
    AlertCircle, Edit, Save,
    Paperclip, Tag, Users, FileText, MapPin, Target,
    CheckCircle, XCircle, ShieldCheck, SendHorizonal, Clock
} from "lucide-react";
import Swal from "sweetalert2";
import config from "../../config";

export default function TicketsPage({ handlePageChange }) {
    const [tickets, setTickets] = useState([]);
    const [admins, setAdmins] = useState([]);
    const [adminPictures, setAdminPictures] = useState({});
    const [search, setSearch] = useState("");
    const [showModal, setShowModal] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [filterStatus, setFilterStatus] = useState("All");
    const [filterPriority, setFilterPriority] = useState("All");
    const [filterApproval, setFilterApproval] = useState("All");

    const [showApprovalModal, setShowApprovalModal] = useState(false);
    const [approvingTicket, setApprovingTicket] = useState(null);
    const [approvalRemark, setApprovalRemark] = useState("");

    const [showAssignModal, setShowAssignModal] = useState(false);
    const [assigningTicket, setAssigningTicket] = useState(null);
    const [selectedAssignee, setSelectedAssignee] = useState("");

    const [editForm, setEditForm] = useState({
        assignees: "", status: "", priority: "", notes: "",
        tags: "", origin: "", calendar: "", timeGoal: "", team: "",
    });

    const API_TICKETS = `${config.API_BASE_URL}/Tickets`;
    const API_ADMINS = `${config.API_BASE_URL}/Admin`;

    const originOptions = ["Email", "Phone", "Chat", "Web Portal", "Walk-in", "Social Media"];
    const calendarOptions = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
    const timeGoalOptions = ["1 hour", "2 hours", "4 hours", "8 hours", "1 day", "2 days", "3 days", "1 week"];
    const teamOptions = ["IT Support", "Network Team", "Development", "Infrastructure", "Security", "Help Desk"];

    const loadTickets = async () => {
        try {
            const res = await fetch(API_TICKETS);
            if (!res.ok) return;
            const data = await res.json();
            if (!data || data.length === 0) return;
            setTickets(data);
        } catch {
            // silent
        }
    };

    const loadAdmins = async () => {
        try {
            const res = await fetch(API_ADMINS);
            if (!res.ok) return;
            const data = await res.json();
            if (!data || data.length === 0) return;
            setAdmins(data);
            // Load profile pictures for each admin
            fetchAdminPictures(data);
        } catch {
            // silent
        }
    };

    const fetchAdminPictures = async (adminList) => {
        const pictures = {};
        await Promise.all(
            adminList.map(async (admin) => {
                try {
                    const res = await fetch(`${API_ADMINS}/get-profile-picture/${admin.username}`);
                    if (res.ok) {
                        const blob = await res.blob();
                        pictures[admin.username] = URL.createObjectURL(blob);
                    }
                } catch {
                    // silent — no pic for this admin
                }
            })
        );
        setAdminPictures(pictures);
    };

    useEffect(() => { loadTickets(); loadAdmins(); }, []);

    // Cleanup object URLs on unmount
    useEffect(() => {
        return () => {
            Object.values(adminPictures).forEach((url) => URL.revokeObjectURL(url));
        };
    }, [adminPictures]);

    const hasPendingRequest = (ticket) =>
        ticket.assignmentRequest &&
        ticket.assignmentRequest !== "" &&
        ticket.assignmentRequestApproved === null;

    const openApprovalModal = (ticket) => {
        setApprovingTicket(ticket);
        setApprovalRemark("");
        setShowApprovalModal(true);
    };

    const handleApprove = async () => {
        try {
            const res = await fetch(`${API_TICKETS}/${approvingTicket.ticketNumber}/approve-assignment`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    approved: true,
                    remark: approvalRemark || "",
                }),
            });
            if (!res.ok) return;

            Swal.fire({
                icon: "success",
                title: "Approved!",
                html: `Ticket assigned to <b>${approvingTicket.assignmentRequest}</b> and moved to <b>In Progress</b>.`,
                timer: 2000,
                showConfirmButton: false,
                background: "#1e293b",
                color: "#f1f5f9",
            });
            setShowApprovalModal(false);
            loadTickets();
        } catch {
            // silent
        }
    };

    const handleReject = async () => {
        if (!approvalRemark.trim()) {
            Swal.fire({
                icon: "warning",
                title: "Remark Required",
                text: "Please provide a reason for rejection.",
                background: "#1e293b",
                color: "#f1f5f9",
            });
            return;
        }
        try {
            const res = await fetch(`${API_TICKETS}/${approvingTicket.ticketNumber}/approve-assignment`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    approved: false,
                    remark: approvalRemark,
                }),
            });
            if (!res.ok) return;

            Swal.fire({
                icon: "error",
                title: "Rejected",
                text: `Request from ${approvingTicket.assignmentRequest} has been rejected.`,
                timer: 1800,
                showConfirmButton: false,
                background: "#1e293b",
                color: "#f1f5f9",
            });
            setShowApprovalModal(false);
            loadTickets();
        } catch {
            // silent
        }
    };

    const openAssignModal = (ticket) => {
        setAssigningTicket(ticket);
        setSelectedAssignee(ticket.assignees || "");
        setShowAssignModal(true);
    };

    const saveAssignee = async () => {
        try {
            const updated = {
                ...assigningTicket,
                assignees: selectedAssignee,
                status: selectedAssignee !== "" ? "Pending" : assigningTicket.status, // ← add this
            };
            const res = await fetch(`${API_TICKETS}/${assigningTicket.ticketNumber}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(updated),
            });
            if (!res.ok) return;
            Swal.fire({ icon: "success", title: "Assigned!", timer: 1200, showConfirmButton: false, background: "#1e293b", color: "#f1f5f9" });
            setShowAssignModal(false);
            loadTickets();
        } catch {
            // silent
        }
    };

    const openViewModal = (ticket) => {
        setSelectedTicket(ticket);
        setEditForm({
            assignees: ticket.assignees || "",
            status: ticket.status || "Open",
            priority: ticket.priority || "Medium",
            notes: ticket.notes || "",
            tags: ticket.tags || "",
            origin: ticket.origin || "",
            calendar: ticket.calendar || "",
            timeGoal: ticket.timeGoal || "",
            team: ticket.team || "",
        });
        setIsEditing(false);
        setShowModal(true);
    };

    const saveChanges = async () => {
        try {
            const updatedTicket = { ...selectedTicket, ...editForm };
            const res = await fetch(`${API_TICKETS}/${selectedTicket.ticketNumber}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(updatedTicket),
            });
            if (!res.ok) return;
            Swal.fire({ icon: "success", title: "Updated!", timer: 1200, showConfirmButton: false, background: "#1e293b", color: "#f1f5f9" });
            setIsEditing(false);
            setSelectedTicket(updatedTicket);
            loadTickets();
        } catch {
            // silent
        }
    };

    const closeModal = () => { setShowModal(false); setSelectedTicket(null); setIsEditing(false); };
    const formatDate = (d) => d ? new Date(d).toLocaleString() : "N/A";

    const getStatusColor = (status) => ({
        Open: "bg-blue-500/20 text-blue-400 border-blue-500/30",
        Pending: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
        "In Progress": "bg-blue-500/20 text-blue-400 border-blue-500/30",
        Resolved: "bg-green-500/20 text-green-400 border-green-500/30",
        Closed: "bg-gray-500/20 text-gray-400 border-gray-500/30",
    }[status] || "bg-slate-500/20 text-slate-400 border-slate-500/30");

    const getPriorityColor = (priority) => ({
        Low: "bg-green-500/20 text-green-400 border-green-500/30",
        Medium: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
        High: "bg-orange-500/20 text-orange-400 border-orange-500/30",
        Critical: "bg-red-500/20 text-red-400 border-red-500/30",
    }[priority] || "bg-slate-500/20 text-slate-400 border-slate-500/30");

    const getApprovalLabel = (ticket) => {
        if (!ticket.assignmentRequest || ticket.assignmentRequest === "") return null;
        if (ticket.assignmentRequestApproved === null) return { label: "Pending", color: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30" };
        if (ticket.assignmentRequestApproved === true) return { label: "Approved", color: "bg-green-500/20 text-green-400 border-green-500/30" };
        if (ticket.assignmentRequestApproved === false) return { label: "Rejected", color: "bg-red-500/20 text-red-400 border-red-500/30" };
        return null;
    };

    const filteredTickets = tickets.filter((ticket) => {
        const matchesSearch =
            ticket.ticketNumber?.toLowerCase().includes(search.toLowerCase()) ||
            ticket.subject?.toLowerCase().includes(search.toLowerCase()) ||
            ticket.name?.toLowerCase().includes(search.toLowerCase());
        const matchesStatus = filterStatus === "All" || ticket.status === filterStatus;
        const matchesPriority = filterPriority === "All" || ticket.priority === filterPriority;
        const matchesApproval =
            filterApproval === "All" ||
            (filterApproval === "Pending" && hasPendingRequest(ticket)) ||
            (filterApproval === "Approved" && ticket.assignmentRequestApproved === true) ||
            (filterApproval === "Rejected" && ticket.assignmentRequestApproved === false) ||
            (filterApproval === "None" && (!ticket.assignmentRequest || ticket.assignmentRequest === ""));
        return matchesSearch && matchesStatus && matchesPriority && matchesApproval;
    });

    const pendingApprovalCount = tickets.filter((t) => hasPendingRequest(t)).length;
    const approvedCount = tickets.filter((t) => t.assignmentRequestApproved === true).length;
    const rejectedCount = tickets.filter((t) => t.assignmentRequestApproved === false).length;

    // Helper: get admin by fullNames
    const getAdminByFullName = (fullName) => admins.find((a) => a.fullNames === fullName);

    // AdminAvatar component
    const AdminAvatar = ({ admin, size = "md" }) => {
        const picUrl = admin ? adminPictures[admin.username] : null;
        const sizeClass = size === "lg" ? "w-14 h-14 text-lg" : size === "sm" ? "w-8 h-8 text-xs" : "w-10 h-10 text-sm";
        const initials = admin
            ? admin.fullNames.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase()
            : "?";
        return picUrl ? (
            <img src={picUrl} alt={admin?.fullNames} className={`${sizeClass} rounded-full object-cover border-2 border-slate-600 shrink-0`} />
        ) : (
            <div className={`${sizeClass} rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center font-bold text-white border-2 border-slate-600 shrink-0`}>
                {initials}
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-slate-100 p-6">
            <div className="max-w-8xl mx-auto">

                {/* HEADER */}
                <div className="flex justify-between items-center mb-8">
                    <div className="flex items-center gap-4">
                        <button onClick={() => handlePageChange("dashboard")} className="p-2.5 rounded-xl bg-slate-800/80 hover:bg-slate-700 border border-slate-700/50 transition-all">
                            <ArrowLeft size={20} />
                        </button>
                        <div>
                            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">Tickets Management</h1>
                            <p className="text-slate-400 text-sm mt-1">Manage tickets and approve assignment requests</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                        <span className="text-slate-400">Total:</span>
                        <span className="px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full font-semibold">{filteredTickets.length}</span>
                    </div>
                </div>

                {/* STATS */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-slate-800/40 border border-yellow-500/20 rounded-2xl p-4 flex items-center gap-4">
                        <div className="p-3 bg-yellow-500/20 rounded-xl"><Clock className="text-yellow-400" size={22} /></div>
                        <div><p className="text-slate-400 text-sm">Pending Requests</p><p className="text-2xl font-bold text-yellow-400">{pendingApprovalCount}</p></div>
                    </div>
                    <div className="bg-slate-800/40 border border-green-500/20 rounded-2xl p-4 flex items-center gap-4">
                        <div className="p-3 bg-green-500/20 rounded-xl"><CheckCircle className="text-green-400" size={22} /></div>
                        <div><p className="text-slate-400 text-sm">Approved</p><p className="text-2xl font-bold text-green-400">{approvedCount}</p></div>
                    </div>
                    <div className="bg-slate-800/40 border border-red-500/20 rounded-2xl p-4 flex items-center gap-4">
                        <div className="p-3 bg-red-500/20 rounded-xl"><XCircle className="text-red-400" size={22} /></div>
                        <div><p className="text-slate-400 text-sm">Rejected</p><p className="text-2xl font-bold text-red-400">{rejectedCount}</p></div>
                    </div>
                </div>

                {/* FILTERS */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <div className="relative">
                        <Search className="absolute left-4 top-3.5 text-slate-400" size={18} />
                        <input className="w-full pl-12 pr-4 py-3 bg-slate-800/60 border border-slate-700/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50" placeholder="Search tickets..." value={search} onChange={(e) => setSearch(e.target.value)} />
                    </div>
                    <select className="px-4 py-3 bg-slate-800/60 border border-slate-700/50 rounded-xl focus:outline-none" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                        <option value="All">All Status</option>
                        <option value="Open">Open</option>
                        <option value="Pending">Pending</option>
                        <option value="In Progress">In Progress</option>
                        <option value="Resolved">Resolved</option>
                        <option value="Closed">Closed</option>
                    </select>
                    <select className="px-4 py-3 bg-slate-800/60 border border-slate-700/50 rounded-xl focus:outline-none" value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)}>
                        <option value="All">All Priority</option>
                        <option value="Low">Low</option>
                        <option value="Medium">Medium</option>
                        <option value="High">High</option>
                        <option value="Critical">Critical</option>
                    </select>
                    <select className="px-4 py-3 bg-slate-800/60 border border-slate-700/50 rounded-xl focus:outline-none" value={filterApproval} onChange={(e) => setFilterApproval(e.target.value)}>
                        <option value="All">All Requests</option>
                        <option value="Pending">Pending Approval</option>
                        <option value="Approved">Approved</option>
                        <option value="Rejected">Rejected</option>
                        <option value="None">No Request</option>
                    </select>
                </div>

                {/* TABLE */}
                <div className="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 overflow-hidden shadow-2xl">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-slate-800/80 border-b border-slate-700/50">
                                <tr>
                                    <th className="p-4 text-left text-sm font-semibold text-slate-300">Ticket #</th>
                                    <th className="p-4 text-left text-sm font-semibold text-slate-300">Subject</th>
                                    <th className="p-4 text-left text-sm font-semibold text-slate-300">Requester</th>
                                    <th className="p-4 text-left text-sm font-semibold text-slate-300">Assignee</th>
                                    <th className="p-4 text-left text-sm font-semibold text-slate-300">Assignment Request</th>
                                    <th className="p-4 text-center text-sm font-semibold text-slate-300">Priority</th>
                                    <th className="p-4 text-center text-sm font-semibold text-slate-300">Status</th>
                                    <th className="p-4 text-left text-sm font-semibold text-slate-300">Created</th>
                                    <th className="p-4 text-center text-sm font-semibold text-slate-300">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredTickets.map((ticket) => {
                                    const approvalInfo = getApprovalLabel(ticket);
                                    const assigneeAdmin = getAdminByFullName(ticket.assignees);
                                    return (
                                        <tr key={ticket.ticketNumber} className={`border-b border-slate-700/30 hover:bg-slate-700/30 transition-colors ${hasPendingRequest(ticket) ? "bg-yellow-500/5" : ""}`}>
                                            <td className="p-4"><span className="font-mono text-blue-400 font-medium">{ticket.ticketNumber}</span></td>
                                            <td className="p-4 max-w-xs">
                                                <div className="truncate font-medium">{ticket.subject}</div>
                                                <div className="text-xs text-slate-400 truncate">{ticket.category}</div>
                                            </td>
                                            <td className="p-4">
                                                <div className="font-medium">{ticket.name}</div>
                                                <div className="text-xs text-slate-400">{ticket.email}</div>
                                            </td>
                                            <td className="p-4">
                                                {ticket.assignees && ticket.assignees !== "" ? (
                                                    <div className="flex items-center gap-2">
                                                        <AdminAvatar admin={assigneeAdmin} size="sm" />
                                                        <span className="text-green-400 text-xs font-medium">{ticket.assignees}</span>
                                                    </div>
                                                ) : (
                                                    <span className="text-slate-500 text-xs italic">Unassigned</span>
                                                )}
                                            </td>
                                            <td className="p-4">
                                                {ticket.assignmentRequest && ticket.assignmentRequest !== "" ? (
                                                    <div className="flex flex-col gap-1">
                                                        <div className="flex items-center gap-1.5">
                                                            <SendHorizonal size={12} className="text-slate-400" />
                                                            <span className="text-xs text-slate-200 font-medium">{ticket.assignmentRequest}</span>
                                                        </div>
                                                        {approvalInfo && (
                                                            <span className={`px-2 py-0.5 rounded-full text-xs border w-fit ${approvalInfo.color}`}>
                                                                {approvalInfo.label}
                                                            </span>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span className="text-slate-600 text-xs italic">—</span>
                                                )}
                                            </td>
                                            <td className="p-4 text-center">
                                                <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getPriorityColor(ticket.priority)}`}>{ticket.priority || "N/A"}</span>
                                            </td>
                                            <td className="p-4 text-center">
                                                <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(ticket.status)}`}>{ticket.status || "N/A"}</span>
                                            </td>
                                            <td className="p-4 text-sm text-slate-300">{formatDate(ticket.createdAt)}</td>
                                            <td className="p-4 text-center">
                                                <div className="flex items-center justify-center gap-1">
                                                    {hasPendingRequest(ticket) && (
                                                        <button
                                                            onClick={() => openApprovalModal(ticket)}
                                                            className="p-2 rounded-lg bg-yellow-500/10 text-yellow-400 hover:bg-yellow-500/20 transition-all border border-yellow-500/20"
                                                            title={`Approve/Reject request from ${ticket.assignmentRequest}`}
                                                        >
                                                            <ShieldCheck size={16} />
                                                        </button>
                                                    )}
                                                    <button onClick={() => openAssignModal(ticket)} className="p-2 rounded-lg bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 transition-all border border-blue-500/20" title="Assign">
                                                        <User size={16} />
                                                    </button>
                                                    <button onClick={() => openViewModal(ticket)} className="p-2 rounded-lg bg-slate-500/10 text-slate-400 hover:bg-slate-500/20 transition-all border border-slate-500/20" title="View">
                                                        <Eye size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                                {filteredTickets.length === 0 && (
                                    <tr><td colSpan="9" className="text-center p-12 text-slate-400">
                                        <FileText size={48} className="mx-auto mb-3 opacity-30" />
                                        <p className="text-lg">No tickets found</p>
                                    </td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* APPROVAL MODAL */}
            {showApprovalModal && approvingTicket && (
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-slate-800 rounded-2xl w-full max-w-md shadow-2xl border border-slate-700/50">
                        <div className="p-6 border-b border-slate-700/50 flex justify-between items-center">
                            <div>
                                <h2 className="text-lg font-bold text-white flex items-center gap-2">
                                    <ShieldCheck size={20} className="text-yellow-400" />
                                    Assignment Request
                                </h2>
                                <p className="text-slate-400 text-xs mt-1">{approvingTicket.ticketNumber}</p>
                            </div>
                            <button onClick={() => setShowApprovalModal(false)} className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 transition-all"><X size={18} /></button>
                        </div>
                        <div className="p-6 space-y-4">
                            <div className="p-3 bg-slate-900/60 rounded-xl border border-slate-700/50 space-y-2">
                                <p className="text-sm font-medium text-slate-200 truncate">{approvingTicket.subject}</p>
                                <div className="flex items-center gap-2 text-xs text-slate-400">
                                    <span>From: <span className="text-slate-200">{approvingTicket.name}</span></span>
                                    <span>•</span>
                                    <span className={`px-2 py-0.5 rounded-full border ${getPriorityColor(approvingTicket.priority)}`}>{approvingTicket.priority}</span>
                                </div>
                            </div>

                            <div className="p-3 bg-yellow-500/10 rounded-xl border border-yellow-500/20 flex items-center gap-3">
                                <SendHorizonal size={18} className="text-yellow-400 shrink-0" />
                                <div>
                                    <p className="text-xs text-slate-400">Requesting to be assigned:</p>
                                    <p className="text-sm font-bold text-yellow-300">{approvingTicket.assignmentRequest}</p>
                                </div>
                            </div>

                            {approvingTicket.assignReason && (
                                <div className="p-3 bg-slate-900/60 rounded-xl border border-slate-700/50">
                                    <p className="text-xs text-slate-400 mb-1">Reason given:</p>
                                    <p className="text-sm text-slate-300">{approvingTicket.assignReason}</p>
                                </div>
                            )}

                            <p className="text-xs text-slate-500 italic">
                                ✓ If approved, <span className="text-slate-300 font-medium">{approvingTicket.assignmentRequest}</span> will be moved to <b>Assignee</b> and status → <span className="text-blue-400">In Progress</span>.
                            </p>

                            <div>
                                <label className="block text-sm text-slate-400 mb-2">Remark <span className="text-xs text-slate-500">(required for rejection)</span></label>
                                <textarea
                                    className="w-full px-4 py-2.5 bg-slate-900 border border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 text-white text-sm resize-none"
                                    rows="3"
                                    placeholder="Add a remark or reason..."
                                    value={approvalRemark}
                                    onChange={(e) => setApprovalRemark(e.target.value)}
                                />
                            </div>

                            <div className="flex gap-2 pt-2">
                                <button onClick={() => setShowApprovalModal(false)} className="flex-1 py-2.5 rounded-xl border border-slate-700 text-slate-400 hover:text-white hover:bg-slate-700 transition-all text-sm">Cancel</button>
                                <button onClick={handleReject} className="flex-1 py-2.5 rounded-xl bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30 transition-all text-sm font-semibold flex items-center justify-center gap-2">
                                    <XCircle size={16} /> Reject
                                </button>
                                <button onClick={handleApprove} className="flex-1 py-2.5 rounded-xl bg-green-500 hover:bg-green-600 text-white font-semibold transition-all text-sm flex items-center justify-center gap-2">
                                    <CheckCircle size={16} /> Approve
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ASSIGN MODAL — with profile pictures */}
            {showAssignModal && assigningTicket && (
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-slate-800 rounded-2xl w-full max-w-md shadow-2xl border border-slate-700/50">
                        <div className="p-6 border-b border-slate-700/50 flex justify-between items-center">
                            <div>
                                <h2 className="text-lg font-bold text-white">Assign Ticket</h2>
                                <p className="text-slate-400 text-xs mt-1">{assigningTicket.ticketNumber}</p>
                            </div>
                            <button onClick={() => setShowAssignModal(false)} className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 transition-all"><X size={18} /></button>
                        </div>
                        <div className="p-6 space-y-4">
                            <p className="text-sm text-slate-300 font-medium truncate">{assigningTicket.subject}</p>

                            <label className="block text-sm text-slate-400 mb-2">Select Assignee</label>

                            {/* Unassign option */}
                            <div
                                onClick={() => setSelectedAssignee("")}
                                className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${selectedAssignee === ""
                                        ? "bg-slate-700 border-blue-500/50 ring-1 ring-blue-500/30"
                                        : "bg-slate-900/50 border-slate-700/50 hover:bg-slate-700/50"
                                    }`}
                            >
                                <div className="w-10 h-10 rounded-full bg-slate-700 border-2 border-slate-600 flex items-center justify-center shrink-0">
                                    <User size={18} className="text-slate-400" />
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-slate-300">— Unassign —</p>
                                    <p className="text-xs text-slate-500">Remove current assignee</p>
                                </div>
                                {selectedAssignee === "" && (
                                    <CheckCircle size={16} className="text-blue-400 ml-auto" />
                                )}
                            </div>

                            {/* Admin list */}
                            <div className="space-y-2 max-h-72 overflow-y-auto pr-1 scrollbar-thin">
                                {admins.map((admin) => (
                                    <div
                                        key={admin.username}
                                        onClick={() => setSelectedAssignee(admin.fullNames)}
                                        className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${selectedAssignee === admin.fullNames
                                                ? "bg-blue-500/10 border-blue-500/50 ring-1 ring-blue-500/30"
                                                : "bg-slate-900/50 border-slate-700/50 hover:bg-slate-700/50"
                                            }`}
                                    >
                                        <AdminAvatar admin={admin} size="md" />
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-semibold text-slate-100 truncate">{admin.fullNames}</p>
                                            <p className="text-xs text-slate-400 truncate">{admin.role} · {admin.username}</p>
                                        </div>
                                        {selectedAssignee === admin.fullNames && (
                                            <CheckCircle size={16} className="text-blue-400 shrink-0" />
                                        )}
                                    </div>
                                ))}
                            </div>

                            <div className="flex gap-2 pt-2">
                                <button onClick={() => setShowAssignModal(false)} className="flex-1 py-2.5 rounded-xl border border-slate-700 text-slate-400 hover:text-white hover:bg-slate-700 transition-all text-sm">Cancel</button>
                                <button onClick={saveAssignee} className="flex-1 py-2.5 rounded-xl bg-blue-500 hover:bg-blue-600 text-white font-semibold transition-all text-sm">Confirm Assign</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* VIEW/EDIT MODAL */}
            {showModal && selectedTicket && (
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-slate-800 rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto relative shadow-2xl border border-slate-700/50">
                        <div className="sticky top-0 bg-slate-800 border-b border-slate-700/50 p-6 flex justify-between items-center z-10">
                            <div>
                                <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">Ticket Details</h2>
                                <p className="text-slate-400 text-sm mt-1">{selectedTicket.ticketNumber}</p>
                            </div>
                            <div className="flex gap-2">
                                {!isEditing
                                    ? <button onClick={() => setIsEditing(true)} className="flex items-center gap-2 px-4 py-2 bg-yellow-500/20 text-yellow-400 rounded-lg hover:bg-yellow-500/30 transition-all text-sm"><Edit size={16} /> Edit</button>
                                    : <button onClick={saveChanges} className="flex items-center gap-2 px-4 py-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 transition-all text-sm"><Save size={16} /> Save Changes</button>
                                }
                                <button onClick={closeModal} className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 transition-all"><X size={20} /></button>
                            </div>
                        </div>

                        <div className="p-6 space-y-6">
                            {selectedTicket.assignmentRequest && selectedTicket.assignmentRequest !== "" && (
                                <div className={`p-4 rounded-xl border flex items-center justify-between ${selectedTicket.assignmentRequestApproved === true ? "bg-green-500/10 border-green-500/30" :
                                    selectedTicket.assignmentRequestApproved === false ? "bg-red-500/10 border-red-500/30" :
                                        "bg-yellow-500/10 border-yellow-500/30"}`}
                                >
                                    <div className="flex items-center gap-3">
                                        <SendHorizonal size={18} className={
                                            selectedTicket.assignmentRequestApproved === true ? "text-green-400" :
                                                selectedTicket.assignmentRequestApproved === false ? "text-red-400" : "text-yellow-400"
                                        } />
                                        <div>
                                            <p className="text-sm font-medium text-slate-200">
                                                Assignment Request by <span className="font-bold">{selectedTicket.assignmentRequest}</span>
                                            </p>
                                            <p className={`text-xs mt-0.5 ${selectedTicket.assignmentRequestApproved === true ? "text-green-400" :
                                                selectedTicket.assignmentRequestApproved === false ? "text-red-400" : "text-yellow-400"}`}
                                            >
                                                {selectedTicket.assignmentRequestApproved === true
                                                    ? `✓ Approved — ${selectedTicket.assignmentRequest} has been assigned`
                                                    : selectedTicket.assignmentRequestApproved === false
                                                        ? `✗ Rejected`
                                                        : "⏳ Waiting for approval..."}
                                            </p>
                                        </div>
                                    </div>
                                    {hasPendingRequest(selectedTicket) && (
                                        <button
                                            onClick={() => { closeModal(); openApprovalModal(selectedTicket); }}
                                            className="px-3 py-1.5 bg-yellow-500/20 text-yellow-400 rounded-lg hover:bg-yellow-500/30 transition-all text-xs font-medium border border-yellow-500/20"
                                        >
                                            Manage
                                        </button>
                                    )}
                                </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-slate-900/50 rounded-xl border border-slate-700/50">
                                <div>
                                    <label className="block text-sm font-medium text-slate-300 mb-2"><Tag className="inline mr-2" size={16} />Tags</label>
                                    {isEditing
                                        ? <input type="text" className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-xl focus:outline-none" value={editForm.tags} onChange={(e) => setEditForm({ ...editForm, tags: e.target.value })} />
                                        : <div className="px-4 py-2.5 bg-slate-800 rounded-xl border border-slate-700">{selectedTicket.tags || "No tags"}</div>}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-300 mb-2"><Users className="inline mr-2" size={16} />Team</label>
                                    {isEditing
                                        ? <select className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-xl focus:outline-none" value={editForm.team} onChange={(e) => setEditForm({ ...editForm, team: e.target.value })}>
                                            <option value="">Select a team</option>
                                            {teamOptions.map((t) => <option key={t} value={t}>{t}</option>)}
                                        </select>
                                        : <div className="px-4 py-2.5 bg-slate-800 rounded-xl border border-slate-700">{selectedTicket.team || "No team"}</div>}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-300 mb-2"><User className="inline mr-2" size={16} />Assignee</label>
                                    {isEditing
                                        ? <select className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-xl focus:outline-none" value={editForm.assignees} onChange={(e) => setEditForm({ ...editForm, assignees: e.target.value })}>
                                            <option value="">Select Assignee</option>
                                            {admins.map((a) => <option key={a.username} value={a.fullNames}>{a.fullNames} ({a.role})</option>)}
                                        </select>
                                        : <div className="px-4 py-2.5 bg-slate-800 rounded-xl border border-slate-700">
                                            {selectedTicket.assignees && selectedTicket.assignees !== "" ? (
                                                <div className="flex items-center gap-2">
                                                    <AdminAvatar admin={getAdminByFullName(selectedTicket.assignees)} size="sm" />
                                                    <span className="text-green-400 font-medium">{selectedTicket.assignees}</span>
                                                </div>
                                            ) : (
                                                <span className="text-slate-500 italic">Unassigned</span>
                                            )}
                                        </div>}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-slate-900/50 rounded-xl border border-slate-700/50">
                                <div>
                                    <label className="block text-sm font-medium text-slate-300 mb-2"><MapPin className="inline mr-2" size={16} />Origin</label>
                                    {isEditing
                                        ? <select className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-xl focus:outline-none" value={editForm.origin} onChange={(e) => setEditForm({ ...editForm, origin: e.target.value })}>
                                            <option value="">Select Origin</option>
                                            {originOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                                        </select>
                                        : <div className="px-4 py-2.5 bg-slate-800 rounded-xl border border-slate-700">{selectedTicket.origin || "Not specified"}</div>}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-300 mb-2"><AlertCircle className="inline mr-2" size={16} />Status</label>
                                    {isEditing
                                        ? <select className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-xl focus:outline-none" value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}>
                                            <option value="Open">Open</option>
                                            <option value="Pending">Pending</option>
                                            <option value="In Progress">In Progress</option>
                                            <option value="Resolved">Resolved</option>
                                            <option value="Closed">Closed</option>
                                        </select>
                                        : <div className={`px-4 py-2.5 rounded-xl border ${getStatusColor(selectedTicket.status)} text-center font-medium`}>{selectedTicket.status}</div>}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-300 mb-2"><AlertCircle className="inline mr-2" size={16} />Priority</label>
                                    {isEditing
                                        ? <select className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-xl focus:outline-none" value={editForm.priority} onChange={(e) => setEditForm({ ...editForm, priority: e.target.value })}>
                                            <option value="Low">Low</option>
                                            <option value="Medium">Medium</option>
                                            <option value="High">High</option>
                                            <option value="Critical">Critical</option>
                                        </select>
                                        : <div className={`px-4 py-2.5 rounded-xl border ${getPriorityColor(selectedTicket.priority)} text-center font-medium`}>{selectedTicket.priority}</div>}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-slate-900/50 rounded-xl border border-slate-700/50">
                                <div>
                                    <label className="block text-sm font-medium text-slate-300 mb-2"><CalendarIcon className="inline mr-2" size={16} />Calendar</label>
                                    {isEditing
                                        ? <select className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-xl focus:outline-none" value={editForm.calendar} onChange={(e) => setEditForm({ ...editForm, calendar: e.target.value })}>
                                            <option value="">Select calendar</option>
                                            {calendarOptions.map((d) => <option key={d} value={d}>{d}</option>)}
                                        </select>
                                        : <div className="px-4 py-2.5 bg-slate-800 rounded-xl border border-slate-700">{selectedTicket.calendar || "Not set"}</div>}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-300 mb-2"><Target className="inline mr-2" size={16} />TimeGoal</label>
                                    {isEditing
                                        ? <select className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-xl focus:outline-none" value={editForm.timeGoal} onChange={(e) => setEditForm({ ...editForm, timeGoal: e.target.value })}>
                                            <option value="">Select TimeGoal</option>
                                            {timeGoalOptions.map((t) => <option key={t} value={t}>{t}</option>)}
                                        </select>
                                        : <div className="px-4 py-2.5 bg-slate-800 rounded-xl border border-slate-700">{selectedTicket.timeGoal || "Not set"}</div>}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-300 mb-2"><FileText className="inline mr-2" size={16} />JQL</label>
                                    <div className="px-4 py-2.5 bg-slate-800 rounded-xl border border-slate-700">{selectedTicket.jql || "Not set"}</div>
                                </div>
                            </div>

                            <div className="p-4 bg-slate-900/50 rounded-xl border border-slate-700/50">
                                <label className="block text-sm font-medium text-slate-300 mb-2"><FileText className="inline mr-2" size={16} />Notes</label>
                                {isEditing
                                    ? <textarea className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-xl focus:outline-none" rows="4" value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} placeholder="Add notes..." />
                                    : <div className="px-4 py-2.5 bg-slate-800 rounded-xl border border-slate-700 min-h-[100px]">{selectedTicket.notes || "No notes"}</div>}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-3">
                                    <h3 className="font-semibold text-lg text-slate-200">Requester Info</h3>
                                    <InfoRow label="Name" value={selectedTicket.name} />
                                    <InfoRow label="Email" value={selectedTicket.email} />
                                    <InfoRow label="Contact" value={selectedTicket.contact} />
                                    <InfoRow label="User ID" value={selectedTicket.userId} />
                                </div>
                                <div className="space-y-3">
                                    <h3 className="font-semibold text-lg text-slate-200">Ticket Info</h3>
                                    <InfoRow label="Category" value={selectedTicket.category} />
                                    <InfoRow label="Created" value={formatDate(selectedTicket.createdAt)} />
                                    <InfoRow label="Deadline" value={formatDate(selectedTicket.deadline)} />
                                    <InfoRow label="Condition" value={selectedTicket.condition} />
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <h3 className="font-semibold text-lg text-slate-200 mb-2">Subject</h3>
                                    <div className="p-4 bg-slate-900/50 rounded-xl border border-slate-700/50">{selectedTicket.subject}</div>
                                </div>
                                <div>
                                    <h3 className="font-semibold text-lg text-slate-200 mb-2">Description</h3>
                                    <div className="p-4 bg-slate-900/50 rounded-xl border border-slate-700/50 max-h-40 overflow-y-auto">{selectedTicket.description || "No description"}</div>
                                </div>
                            </div>

                            {(selectedTicket.attachment || selectedTicket.subtask || selectedTicket.followup !== null) && (
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-slate-900/50 rounded-xl border border-slate-700/50">
                                    {selectedTicket.attachment && (
                                        <div>
                                            <label className="text-sm text-slate-400 flex items-center gap-2 mb-1"><Paperclip size={16} />Attachment</label>
                                            <button className="text-blue-400 hover:underline text-sm">View Attachment</button>
                                        </div>
                                    )}
                                    {selectedTicket.subtask && <InfoRow label="Subtask" value={selectedTicket.subtask} />}
                                    {selectedTicket.followup !== null && <InfoRow label="Follow-up" value={selectedTicket.followup ? "Yes" : "No"} />}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function InfoRow({ label, value }) {
    return (
        <div>
            <label className="text-sm text-slate-400 mb-1 block">{label}</label>
            <div className="px-3 py-2 bg-slate-900/50 rounded-lg border border-slate-700/50 text-sm">{value || "N/A"}</div>
        </div>
    );
}